import { useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { trackViewItemList } from '@browsing/tracking';

import { TRACKING_CONSTANTS, INTERSECTION_OBSERVER_CONFIG } from '../constants';
import { TrackingItemType } from '../types';

/**
 * Custom hook for handling view tracking with IntersectionObserver
 * Tracks when the continue shopping component comes into view
 * Fires tracking for the current tab's items initially and on tab changes
 * Uses react-intersection-observer for consistency with the rest of the codebase
 */
export const useViewTracking = (
    containerRef: React.RefObject<HTMLElement>,
    trackingItems: TrackingItemType[],
    currentTabId: string,
): void => {
    const { ref: intersectionRef, inView } = useInView({
        threshold: INTERSECTION_OBSERVER_CONFIG.THRESHOLD,
        triggerOnce: false,
    });
    const trackedTabsRef = useRef<Set<string>>(new Set());

    useEffect(() => {
        const element = containerRef.current;
        if (element && typeof intersectionRef === 'function') {
            intersectionRef(element);
        }
    }, [containerRef, intersectionRef]);

    useEffect(() => {
        if (
            inView &&
            trackingItems.length > 0 &&
            currentTabId &&
            !trackedTabsRef.current.has(currentTabId)
        ) {
            trackedTabsRef.current.add(currentTabId);
            trackViewItemList(
                {
                    context: TRACKING_CONSTANTS.CONTEXT,
                    element: TRACKING_CONSTANTS.ELEMENT,
                    itemListName: TRACKING_CONSTANTS.ITEM_LIST_NAME,
                    items: trackingItems,
                },
                {
                    customMapper: () => ({
                        item_list_name: TRACKING_CONSTANTS.ITEM_LIST_NAME,
                    }),
                },
            );
        }
    }, [inView, trackingItems, currentTabId]);
};
